import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Landmark } from 'lucide-react';
import { matchingAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

export function AccountingReconciledEvidenceTierPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await matchingAPI.previewAccountingReconciledEvidenceTier();
      setPreview(res.data);
    } catch (err) {
      setPreview({ error: getErrorMessage(err) });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Arm auto-confirm for ${preview?.would_auto_confirm_count || 0} accounting-reconciled, ` +
      'pinned match(es)? This is a one-time switch -- confirms then happen automatically on the ' +
      'next scheduled auto-matching run, through the normal confirm path. Not reversible from ' +
      'here (revert individual matches via Reconciliation if needed).'
    )) return;
    setApplyLoading(true);
    try {
      const res = await matchingAPI.applyAccountingReconciledEvidenceTier();
      setApplyResult(res.data);
    } catch (err) {
      setApplyResult({ error: getErrorMessage(err) });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Accounting-Reconciled Evidence Tier</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Uses Pennylane's own bank-reconciliation state (pulled read-only, never live-queried
          from the matching chain) as match evidence: a transaction reconciled against a single,
          identified invoice can auto-confirm once armed; reconciled-but-unpinned (a split
          payment, or an unresolved linked invoice) is a ranking boost only. Xero is display-only
          (Phase 2B) and never feeds this tier -- its API exposes no invoice-linking FK strong
          enough to pin a pairing. A uniquely-pinned pair the ordinary matching chain never
          proposed on its own can self-originate straight from the pin (no prior chain proposal
          required) once armed; a pin whose amounts diverge beyond the ordinary match tolerance
          never self-confirms, it only ever shows up in the amount-divergent bucket below,
          reserved for a future partial-payments slice.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="accounting-evidence-tier-preview">
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && !preview.armed && preview.would_auto_confirm_count > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
              data-testid="accounting-evidence-tier-apply"
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Arm Auto-Confirm ({preview.would_auto_confirm_count})
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2" data-testid="accounting-evidence-tier-preview-result">
                <div className="flex flex-wrap gap-2">
                  {preview.armed && (
                    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
                      Armed
                    </Badge>
                  )}
                  {preview.tallies && Object.entries(preview.tallies).map(([key, count]) => (
                    <Badge key={key} variant="outline" className="text-[10px]" data-testid={'accounting-evidence-tally-' + key}>
                      {key}: {count}
                    </Badge>
                  ))}
                  {preview.truncated && (
                    <span className="text-amber-600 text-xs">Truncated at scan cap</span>
                  )}
                </div>
                {preview.would_auto_confirm && preview.would_auto_confirm.length > 0 && (
                  <ul className="text-xs space-y-1" data-testid="accounting-evidence-would-auto-confirm-list">
                    {preview.would_auto_confirm.map((w) => (
                      <li key={w.match_id || `${w.transaction_id}:${w.invoice_id}`} className="font-mono">
                        {w.transaction_id} · {w.invoice_id} · {w.source_system} · {w.match_basis}
                        {w.self_minted && (
                          <Badge variant="outline" className="ml-1 text-[9px] align-middle">self-originated</Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {preview.amount_divergent_pin && preview.amount_divergent_pin.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      Amount-divergent pins (reserved for partial payments, never auto-confirm):
                    </p>
                    <ul className="text-xs space-y-1" data-testid="accounting-evidence-amount-divergent-list">
                      {preview.amount_divergent_pin.map((d) => (
                        <li key={`${d.transaction_id}:${d.invoice_id}`} className="font-mono text-amber-700 dark:text-amber-400">
                          {d.transaction_id} · {d.invoice_id} · {d.source_system} · {d.match_basis}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {preview.blocked && preview.blocked.length > 0 && (
                  <ul className="text-xs space-y-1" data-testid="accounting-evidence-blocked-list">
                    {preview.blocked.map((b) => (
                      <li key={b.match_id} className="font-mono text-amber-700 dark:text-amber-400">
                        {b.transaction_id} · {b.invoice_id} · blocked: {b.reason}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground">{preview.message}</p>
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="accounting-evidence-tier-apply-result">
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Armed:</strong> {applyResult.newly_armed ? 'yes (just now)' : 'already armed'}</p>
                <p className="text-muted-foreground">{applyResult.message}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
